import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/authConfig';
import { clearSessionScopedState, RETURN_PATH_KEY } from '../config/sessionKeys';
import apiService from '../services/apiService';
import logger from '../utils/logger';

const AuthContext = createContext(null);

// Auth bypass is explicit env-only to avoid accidental hostname-based bypasses.
const DEV_MODE = process.env.REACT_APP_DISABLE_AUTH === 'true';

const msalInstance = new PublicClientApplication(msalConfig);

// MSAL error codes that only the user can resolve by signing in again. Anything else
// (network, a renewal iframe timing out) is transient and must not end the session.
const INTERACTION_REQUIRED_CODES = new Set([
    'interaction_required',
    'login_required',
    'consent_required',
    'no_tokens_found',
    'refresh_token_expired',
    'bad_token',
    'native_account_unavailable',
    'ux_not_allowed',
    'invalid_grant',
    'no_account',
]);

const isInteractionRequired = (error) => (
    error?.name === 'InteractionRequiredAuthError'
    || INTERACTION_REQUIRED_CODES.has(error?.errorCode)
);

// The user closed or cancelled the sign-in popup: not a failure, nothing to report.
const SIGN_IN_CANCELLED_CODES = new Set(['user_cancelled', 'popup_window_closed']);
const isSignInCancelled = (error) => SIGN_IN_CANCELLED_CODES.has(error?.errorCode);

const rememberReturnPath = () => {
    try {
        sessionStorage.setItem(RETURN_PATH_KEY, `${window.location.pathname}${window.location.search}`);
    } catch (e) { /* storage unavailable — they land on the dashboard instead */ }
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [accessToken, setAccessToken] = useState(null);
    const [error, setError] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    // Refs so a burst of failing requests expires the session once, and so the
    // re-sign-in still knows who was signed in after the MSAL cache is cleared.
    const sessionExpiredRef = useRef(false);
    const expiredAccountRef = useRef(null);

    useEffect(() => {
        const initializeMsal = async () => {
            if (DEV_MODE) {
                logger.info('Dev mode: Authentication bypassed');
                setUser({ name: 'Dev User', username: 'dev@localhost' });
                setIsAuthenticated(true);
                setIsLoading(false);
                return;
            }

            try {
                await msalInstance.initialize();
                await msalInstance.handleRedirectPromise();

                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                    setUser(accounts[0]);
                    setIsAuthenticated(true);

                    try {
                        const response = await msalInstance.acquireTokenSilent({
                            ...loginRequest,
                            account: accounts[0],
                        });
                        setAccessToken(response.accessToken);
                        apiService.setAccessToken(response.accessToken);
                    } catch (tokenError) {
                        logger.error('Failed to acquire token silently', tokenError);
                        setError('Failed to refresh authentication token');
                        // Without this the app renders the full dashboard for a session
                        // that holds no token: every request 401s while the header,
                        // navigation and empty states all look normal. The error above
                        // is only shown on the pre-auth routes, so nothing surfaced.
                        // The workspace slug is kept and the page remembered, so the
                        // sign-in screen is that workspace's and leads back here.
                        rememberReturnPath();
                        setUser(null);
                        setIsAuthenticated(false);
                        apiService.setAccessToken(null);
                    }
                }
            } catch (initError) {
                logger.error('MSAL initialization error', initError);
                setError('Authentication initialization failed');
            } finally {
                setIsLoading(false);
            }
        };

        initializeMsal();
    }, []);

    const login = async () => {
        try {
            setError(null);
            setIsLoading(true);

            const response = await msalInstance.loginPopup(loginRequest);

            msalInstance.setActiveAccount(response.account);
            setUser(response.account);
            setIsAuthenticated(true);
            setAccessToken(response.accessToken);
            apiService.setAccessToken(response.accessToken);

            return response;
        } catch (loginError) {
            // Still re-thrown so a caller can tell; the sign-in screens catch it (QA L-1).
            if (isSignInCancelled(loginError)) {
                logger.info('Sign-in cancelled by the user');
            } else {
                logger.error('Login failed', loginError);
                setError('Login failed. Please try again.');
            }
            throw loginError;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        try {
            setError(null);

            const account = msalInstance.getActiveAccount();
            await msalInstance.logoutPopup({
                account,
                postLogoutRedirectUri: window.location.origin,
            });

            setUser(null);
            setIsAuthenticated(false);
            setAccessToken(null);
            apiService.setAccessToken(null);
            // The account and token go, but the tenant hints used to stay: nothing
            // reloads the page here, so the next person to sign in on this tab kept
            // sending the previous user's workspace as X-Tenant-Slug and hit a 403
            // they had no way to explain.
            clearSessionScopedState();
        } catch (logoutError) {
            // A cancelled popup used to leave the session fully intact — token in
            // sessionStorage, data still on screen — while the error was rendered
            // nowhere inside the authenticated tree. Tear down regardless; the worst
            // case is the user signs in again.
            logger.error('Logout failed', logoutError);
            setUser(null);
            setIsAuthenticated(false);
            setAccessToken(null);
            apiService.setAccessToken(null);
            clearSessionScopedState();
            throw logoutError;
        }
    };

    /**
     * Get an access token without user interaction.
     *
     * `forceRefresh` skips MSAL's token cache: after the API has refused a token,
     * asking for "a token" again just hands back the same one. With `interactive`
     * (only ever from a click handler) a failed silent attempt falls back to a popup.
     * Background callers never pass it — a popup nobody asked for is blocked by the
     * browser at best, and at worst is what signed a working user out mid-use.
     *
     * A silent failure is rethrown with `interactionRequired` set when only the user
     * can fix it (refresh token expired, consent, account gone), as opposed to a
     * transient failure such as a renewal iframe timing out.
     */
    const getAccessToken = async ({ forceRefresh = false, interactive = false } = {}) => {
        try {
            const account = msalInstance.getActiveAccount();
            if (!account) {
                throw Object.assign(new Error('No active account. Please login again.'), {
                    errorCode: 'no_account',
                });
            }

            const response = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account,
                forceRefresh,
            });
            setAccessToken(response.accessToken);
            apiService.setAccessToken(response.accessToken);
            return response.accessToken;
        } catch (silentError) {
            logger.error('Silent token acquisition failed', silentError);
            if (!interactive) {
                const err = new Error('Silent token acquisition failed');
                err.interactionRequired = isInteractionRequired(silentError);
                err.cause = silentError;
                throw err;
            }
            try {
                const response = await msalInstance.acquireTokenPopup(loginRequest);
                setAccessToken(response.accessToken);
                apiService.setAccessToken(response.accessToken);
                return response.accessToken;
            } catch (interactiveError) {
                logger.error('Interactive token acquisition failed', interactiveError);
                setError('Failed to refresh authentication. Please login again.');
                throw interactiveError;
            }
        }
    };

    /**
     * The session can no longer be renewed silently.
     *
     * Deliberately not a logout: no popup, no redirect, and the workspace the user is
     * in (pp_workspace_slug) survives. The dead tokens are dropped locally so nothing
     * keeps sending them, and where the user was is remembered so that signing in
     * again — from the prompt, or via /login/:slug after a reload — puts them back.
     * The page stays mounted underneath the prompt, so nothing they had open is lost.
     */
    const expireSession = async () => {
        if (sessionExpiredRef.current) return;
        sessionExpiredRef.current = true;
        rememberReturnPath();

        expiredAccountRef.current = msalInstance.getActiveAccount() || user;
        setAccessToken(null);
        apiService.setAccessToken(null);
        setSessionExpired(true);
        try {
            // Local only — unlike logoutPopup/logoutRedirect this neither ends the
            // session at the identity provider nor navigates anywhere.
            await msalInstance.clearCache(
                expiredAccountRef.current ? { account: expiredAccountRef.current } : undefined,
            );
        } catch (clearError) {
            logger.warn('Clearing the MSAL cache failed', clearError);
        }
    };

    /** Sign back in after expiry. Call it only from a click — it opens a popup. */
    const reauthenticate = async () => {
        const previous = expiredAccountRef.current;
        try {
            setError(null);
            const response = await msalInstance.loginPopup({
                ...loginRequest,
                ...(previous?.username ? { loginHint: previous.username } : {}),
            });

            // Someone else signed in on this tab: nothing on screen belongs to them,
            // so start clean rather than show the previous user's dealership.
            if (previous?.homeAccountId && response.account?.homeAccountId
                && previous.homeAccountId !== response.account.homeAccountId) {
                clearSessionScopedState();
                window.location.assign('/');
                return false;
            }

            msalInstance.setActiveAccount(response.account);
            setUser(response.account);
            setIsAuthenticated(true);
            setAccessToken(response.accessToken);
            apiService.setAccessToken(response.accessToken);
            try { sessionStorage.removeItem(RETURN_PATH_KEY); } catch (e) { /* nothing stored */ }
            sessionExpiredRef.current = false;
            expiredAccountRef.current = null;
            setSessionExpired(false);
            return true;
        } catch (loginError) {
            logger.error('Re-authentication failed', loginError);
            setError('Sign-in didn’t go through. Please try again.');
            return false;
        }
    };

    // Hand the renewal routine to the API client so a 401 can be recovered from
    // instead of surfacing as empty data. `getAccessToken` is redefined on every
    // render but holds no state of its own, so re-registering it is harmless.
    useEffect(() => {
        apiService.setTokenRefresher(getAccessToken);
        return () => apiService.setTokenRefresher(null);
    });

    const value = {
        isAuthenticated,
        user,
        isLoading,
        accessToken,
        error,
        sessionExpired,
        login,
        logout,
        getAccessToken,
        expireSession,
        reauthenticate,
        msalInstance,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
